import { Trophy, CheckCircle, Clock, Dumbbell } from 'lucide-react';
import { Button } from '../ui';

export function OptionalComplete({
  phaseNum,
  exercises,
  exerciseData,
  sessionState,
  onFinish,
  isSaving,
}) {
  // Calculate stats
  const documentedExercises = exercises.filter(ex => {
    const data = exerciseData[ex.id];
    if (!data?.sets) return false;
    return data.sets.some(set => 
      Object.keys(set).some(key => key !== 'notes' && set[key])
    );
  });

  const startTime = new Date(sessionState.startedAt);
  const endTime = sessionState.completedAt 
    ? new Date(sessionState.completedAt) 
    : new Date();
  const durationMinutes = Math.round((endTime - startTime) / 60000);

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="p-4 space-y-4">
        {/* Success Banner */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white text-center">
          <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Trophy className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Great Work!</h2>
          <p className="text-green-100">
            Phase {phaseNum} Optional Session Complete
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
            <Dumbbell className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">
              {documentedExercises.length}/{exercises.length}
            </p>
            <p className="text-xs text-gray-500">Exercises Documented</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
            <Clock className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{durationMinutes}</p>
            <p className="text-xs text-gray-500">Minutes</p>
          </div>
        </div>

        {/* Exercise Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Exercise Summary</h3>
          </div>
          
          <div className="divide-y divide-gray-100">
            {exercises.map((exercise, index) => {
              const data = exerciseData[exercise.id];
              const hasData = data?.sets?.some(set => 
                Object.keys(set).some(key => key !== 'notes' && set[key])
              );
              
              return (
                <div key={exercise.id} className="p-4 flex items-center">
                  <div className={`
                    h-8 w-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0
                    ${hasData ? 'bg-green-100' : 'bg-gray-100'}
                  `}>
                    {hasData ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <span className="text-sm font-medium text-gray-500">{index + 1}</span>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${hasData ? 'text-gray-900' : 'text-gray-500'}`}>
                      {exercise.exercise}
                    </p>
                    {hasData && data?.sets && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {data.sets.filter(s => Object.keys(s).some(k => k !== 'notes' && s[k])).length} sets documented
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Encouragement */}
        <div className="bg-primary/5 rounded-xl p-4 text-center">
          <p className="text-primary font-medium">
            Keep up the great work! 💪
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Optional sessions help build strength and endurance.
          </p>
        </div>
      </div>

      {/* Bottom Button */}
      <div className="p-4 mt-auto">
        <Button
          onClick={onFinish}
          fullWidth
          size="lg"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Done'
          )}
        </Button>
      </div>
    </div>
  );
}